import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiClient } from '@services/api';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import { useSessionStore } from '@stores/sessionStore';
import { emitActivityChanged } from '@utils/activityEvents';
import './SmartFridge.scss';

// Shelf tracking: the guest opens the fridge from here, takes what they want, closes the door, and
// gets the receipt. The fridge's cameras work out what was taken; the server charges the room.

export interface FridgeMenuItem {
  id: number;
  name: string;
  price?: number;
  imagePath?: string;
}

interface FridgeQuestion { quantity: number; note?: string; candidateMenuItemIds: number[] }

interface FridgeSession {
  id: string;
  state: string;
  doorOpened: boolean;
  questions?: FridgeQuestion[];
  orderNumber?: string;
  currencySymbol?: string;
  chargedAmount?: number;
  items?: { menuItemId: number; quantity: number; unitPrice: number; lineTotal: number }[];
}

interface Props {
  hotelSlug: string;
  stationId: number;
  items: FridgeMenuItem[];
  currencySymbol: string;
  t: (key: string) => string;
  /** False when the page already shows the menu (a station with both a fridge and basket scanning). */
  showMenu?: boolean;
}

// States after which nothing more happens from the guest's side.
const DONE = new Set(['charged', 'no_items', 'voided', 'unused', 'failed', 'review', 'restocked']);

const tr = (t: Props['t'], key: string, fallback: string) => {
  const v = t(key);
  return v && v !== key ? v : fallback;
};

export default function SmartFridge({ hotelSlug, stationId, items, currencySymbol, t, showMenu = true }: Props) {
  const { isDeviceTrusted } = useSessionStore();
  const base = `/api/v1/${hotelSlug}/stations/${stationId}/fridge`;
  const [status, setStatus] = useState<{ canOpen: boolean; reason?: string } | null>(null);
  const [session, setSession] = useState<FridgeSession | null>(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<number, number | null>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const byId = new Map(items.map(i => [i.id, i]));
  const sym = currencySymbol;

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiClient.get<{ canOpen: boolean; reason?: string; activeSessionId?: string }>(base);
      setStatus(res.data);
      // Back to an opening still in progress (e.g. the page was reloaded while the door was open)
      if (res.data.activeSessionId && !session) follow(res.data.activeSessionId);
    } catch {
      setStatus({ canOpen: false, reason: tr(t, 'fridge.unavailable', 'The fridge is not available right now.') });
    }
  }, [base]);

  useEffect(() => { loadStatus(); return () => stopPolling(); }, [loadStatus]);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const follow = (id: string) => {
    stopPolling();
    const tick = async () => {
      try {
        const res = await apiClient.get<FridgeSession>(`${base}/sessions/${id}`);
        setSession(res.data);
        if (DONE.has(res.data.state)) {
          stopPolling();
          if (res.data.state === 'charged') emitActivityChanged();
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
  };

  const open = async () => {
    // Fridge items are charged to the room — a browse-only device verifies first.
    if (!isDeviceTrusted()) {
      promptVerifyToOrder(() => open());
      return;
    }
    setOpening(true);
    setError(null);
    try {
      const res = await apiClient.post<FridgeSession>(`${base}/open`, {});
      setPicks({});
      setSession(res.data);
      follow(res.data.id);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { Error?: string; error?: string } } };
      setError(e.response?.data?.Error || e.response?.data?.error || tr(t, 'fridge.openFailed', "The fridge couldn't be opened. Please try again."));
      loadStatus();
    } finally {
      setOpening(false);
    }
  };

  const answer = async () => {
    if (!session?.questions) return;
    try {
      const res = await apiClient.post<FridgeSession>(`${base}/sessions/${session.id}/answers`, {
        picks: session.questions.map((_, i) => picks[i] ?? null),
      });
      setSession(res.data);
      if (!DONE.has(res.data.state)) follow(res.data.id);
      else if (res.data.state === 'charged') emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { Error?: string } } };
      setError(e.response?.data?.Error || tr(t, 'fridge.answerFailed', 'Something went wrong — please try again.'));
    }
  };

  const reset = () => {
    stopPolling();
    setSession(null);
    setError(null);
    loadStatus();
  };

  // ── Render ──

  const state = session?.state;
  const step = (icon: string, title: string, note?: string, spinning = false) => (
    <div className="fridge-step">
      <div className={`fridge-icon ${spinning ? 'pulse' : ''}`}>{icon}</div>
      <h3>{title}</h3>
      {note && <p>{note}</p>}
    </div>
  );

  let body: ReactNode;
  if (!session) {
    body = (
      <>
        {step('🧊', tr(t, 'fridge.title', 'Help yourself'),
          tr(t, 'fridge.howItWorks', 'Open the fridge, take what you like and close the door. We see what you took and charge it to your room.'))}
        {error && <div className="fridge-error">⚠️ {error}</div>}
        {status && !status.canOpen && <div className="fridge-note">{status.reason}</div>}
        <button className="fridge-btn primary" onClick={open} disabled={opening || !status?.canOpen}>
          {opening ? tr(t, 'fridge.opening', 'Opening…') : `🔓 ${tr(t, 'fridge.open', 'Open fridge')}`}
        </button>
      </>
    );
  } else if (state === 'capturing_before' || state === 'unlocking') {
    body = step('⏳', tr(t, 'fridge.gettingReady', 'Getting the fridge ready…'), undefined, true);
  } else if (state === 'open' && !session.doorOpened) {
    body = step('🔓', tr(t, 'fridge.isOpen', 'The fridge is unlocked'), tr(t, 'fridge.pullDoor', 'Pull the door to open it.'));
  } else if (state === 'open') {
    body = step('🧃', tr(t, 'fridge.takeItems', 'Take what you like'), tr(t, 'fridge.closeDoor', 'Close the door when you’re done — it locks by itself.'));
  } else if (state === 'capturing_after' || state === 'analyzing') {
    body = step('🔍', tr(t, 'fridge.checking', 'Checking what you took…'), tr(t, 'fridge.fewSeconds', 'This takes a few seconds.'), true);
  } else if (state === 'awaiting_guest' && session.questions) {
    const unanswered = session.questions.some((_, i) => !(i in picks));
    body = (
      <>
        {step('❓', tr(t, 'fridge.whichOne', 'Which one did you take?'))}
        {session.questions.map((q, i) => (
          <div key={i} className="fridge-question">
            <p>{q.quantity > 1 ? `${q.quantity}× ` : ''}{q.note || tr(t, 'fridge.notSure', "We couldn't tell which one")}</p>
            <div className="fridge-candidates">
              {q.candidateMenuItemIds.filter(id => byId.has(id)).map(id => {
                const mi = byId.get(id)!;
                return (
                  <button key={id} className={`fridge-candidate ${picks[i] === id ? 'picked' : ''}`} onClick={() => setPicks(p => ({ ...p, [i]: id }))}>
                    {mi.imagePath && <img src={mi.imagePath} alt="" />}
                    <span>{mi.name}</span>
                    {mi.price != null && mi.price > 0 && <small>{sym}{mi.price.toFixed(2)}</small>}
                  </button>
                );
              })}
              <button className={`fridge-candidate none ${picks[i] === null ? 'picked' : ''}`} onClick={() => setPicks(p => ({ ...p, [i]: null }))}>
                <span>{tr(t, 'fridge.noneOfThese', 'None of these')}</span>
              </button>
            </div>
          </div>
        ))}
        {error && <div className="fridge-error">⚠️ {error}</div>}
        <button className="fridge-btn primary" disabled={unanswered} onClick={answer}>
          {tr(t, 'fridge.confirm', 'Confirm')}
        </button>
      </>
    );
  } else if (state === 'charged') {
    const s = session.currencySymbol || sym;
    body = (
      <>
        {step('✅', tr(t, 'fridge.charged', 'All set — charged to your room'), session.orderNumber ? `#${session.orderNumber}` : undefined)}
        <div className="fridge-receipt">
          {(session.items ?? []).map(i => (
            <div key={i.menuItemId} className="fridge-receipt-line">
              <span>{i.quantity}× {byId.get(i.menuItemId)?.name ?? ''}</span>
              <span>{i.lineTotal > 0 ? `${s}${i.lineTotal.toFixed(2)}` : '—'}</span>
            </div>
          ))}
          <div className="fridge-receipt-line total">
            <span>{tr(t, 'fridge.total', 'Total')}</span>
            <span>{s}{(session.chargedAmount ?? 0).toFixed(2)}</span>
          </div>
        </div>
        <button className="fridge-btn ghost" onClick={reset}>{tr(t, 'fridge.done', 'Done')}</button>
      </>
    );
  } else if (state === 'no_items') {
    body = (<>{step('👍', tr(t, 'fridge.nothingTaken', 'Nothing was taken — no charge.'))}<button className="fridge-btn ghost" onClick={reset}>{tr(t, 'fridge.done', 'Done')}</button></>);
  } else if (state === 'review' || state === 'voided') {
    body = (<>{step('🙏', tr(t, 'fridge.thanks', 'Thank you!'), tr(t, 'fridge.teamWillConfirm', 'Our team will confirm what you took shortly.'))}<button className="fridge-btn ghost" onClick={reset}>{tr(t, 'fridge.done', 'Done')}</button></>);
  } else if (state === 'unused') {
    body = (<>{step('🔒', tr(t, 'fridge.lockedAgain', 'The fridge locked again'), tr(t, 'fridge.notOpened', "The door wasn't opened in time — nothing was charged."))}<button className="fridge-btn primary" onClick={reset}>{tr(t, 'fridge.tryAgain', 'Try again')}</button></>);
  } else {
    body = (<>{step('⚠️', tr(t, 'fridge.failed', "The fridge couldn't be opened"), tr(t, 'fridge.failedNote', 'Nothing was charged. Please try again or contact reception.'))}<button className="fridge-btn primary" onClick={reset}>{tr(t, 'fridge.tryAgain', 'Try again')}</button></>);
  }

  return (
    <div className="smart-fridge">
      <div className="fridge-card">{body}</div>
      {showMenu && items.length > 0 && (
        <div className="fridge-menu">
          <h3>{tr(t, 'fridge.inside', "What's inside")}</h3>
          {items.map(i => (
            <div key={i.id} className="fridge-menu-line">
              {i.imagePath ? <img src={i.imagePath} alt="" /> : <span className="fridge-menu-placeholder">🧃</span>}
              <span className="fridge-menu-name">{i.name}</span>
              <span className="fridge-menu-price">{i.price != null && i.price > 0 ? `${sym}${i.price.toFixed(2)}` : tr(t, 'fridge.free', 'Free')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
